import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { usePageTransition } from "@/hooks/usePageTransition";
import IntakeTransitionOverlay from "@/components/feature/IntakeTransitionOverlay";
import IntakeProgressBar from "@/components/feature/IntakeProgressBar";
import Model3DViewer, {
  type Model3DEntry,
  type Model3DRenderMode,
  type Model3DTransformMode,
} from "@/components/feature/Model3DViewer";
import { adPanelContent } from "@/mocks/intake-pages";
import { getTotalIntakeSteps } from "@/lib/stage1Questionnaire";

type ScanInputMethod = "scan" | "upload";

// Preloaded sample models served as static assets from public/3d-models — let
// the Upload tab show a working preview out of the box instead of an empty
// dropzone, and double as a side-by-side comparison demo (see
// Model3DViewer's multi-model ghosting). Static assets resolve their own
// relative texture paths via three.js's MTLLoader (it derives the base path
// from the .mtl URL itself), so no textureUrls remap is needed here the way
// uploaded blob files require.
const PRESET_MODELS: Model3DEntry[] = [
  {
    id: "sample-scan",
    url: "/3d-models/7f264116-6835-4ad3-be02-3eaf1b441cdb.obj",
    type: "obj",
    mtlUrl: "/3d-models/7f264116-6835-4ad3-be02-3eaf1b441cdb.mtl",
    label: "Right leg",
  },
  {
    id: "sample-scan-legacy",
    url: "/3d-models/a96037a3-47a6-4a2c-9d10-0cd01d76a14b.obj",
    type: "obj",
    mtlUrl: "/3d-models/a96037a3-47a6-4a2c-9d10-0cd01d76a14b.mtl",
    label: "Right leg by mother in law",
  },
];

// Shared between the normal (native-fullscreen-capable) container and the
// portal-rendered CSS-fallback container so the render/wireframe/fullscreen
// controls stay in sync regardless of which one is currently mounted.
const TRANSFORM_TOOLS: {
  mode: Exclude<Model3DTransformMode, null>;
  label: string;
  icon: string;
}[] = [
  { mode: "translate", label: "Move", icon: "ri-drag-move-2-line" },
  { mode: "rotate", label: "Rotate", icon: "ri-refresh-line" },
  { mode: "scale", label: "Scale", icon: "ri-expand-diagonal-line" },
];

function ModelViewerPanel({
  models,
  activeModelId,
  onSelectModel,
  onRemoveModel,
  onAddModel,
  renderMode,
  setRenderMode,
  transformMode,
  setTransformMode,
  isViewerFullscreen,
  onToggleFullscreen,
}: {
  models: Model3DEntry[];
  activeModelId: string;
  onSelectModel?: (id: string) => void;
  onRemoveModel?: (id: string) => void;
  onAddModel?: () => void;
  renderMode: Model3DRenderMode;
  setRenderMode: (mode: Model3DRenderMode) => void;
  transformMode: Model3DTransformMode;
  setTransformMode: (mode: Model3DTransformMode) => void;
  isViewerFullscreen: boolean;
  onToggleFullscreen: () => void;
}) {
  return (
    <div className="relative w-full h-full">
      <Model3DViewer
        models={models}
        activeId={activeModelId}
        onSelectModel={onSelectModel}
        renderMode={renderMode}
        transformMode={transformMode}
      />

      <div className="absolute top-2 left-2 flex flex-col gap-1 z-10">
        <div className="flex gap-1 p-1 rounded-lg bg-foreground-950/60">
          <button
            type="button"
            onClick={() => setRenderMode("solid")}
            className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors cursor-pointer ${
              renderMode === "solid"
                ? "bg-white text-foreground-900"
                : "text-white/80 hover:text-white"
            }`}
          >
            Solid
          </button>
          <button
            type="button"
            onClick={() => setRenderMode("wireframe")}
            className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors cursor-pointer ${
              renderMode === "wireframe"
                ? "bg-white text-foreground-900"
                : "text-white/80 hover:text-white"
            }`}
          >
            Wireframe
          </button>
        </div>

        <div className="flex gap-1 p-1 rounded-lg bg-foreground-950/60">
          {TRANSFORM_TOOLS.map((tool) => (
            <button
              key={tool.mode}
              type="button"
              onClick={() =>
                setTransformMode(transformMode === tool.mode ? null : tool.mode)
              }
              aria-label={tool.label}
              title={tool.label}
              className={`w-6 h-6 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                transformMode === tool.mode
                  ? "bg-white text-foreground-900"
                  : "text-white/80 hover:text-white"
              }`}
            >
              <i className={`${tool.icon} text-xs`}></i>
            </button>
          ))}
        </div>
      </div>

      {models.length > 0 && (
        <div className="absolute bottom-2 left-2 right-2 flex items-center gap-1.5 z-10 overflow-x-auto">
          {models.map((m) => (
            <div
              key={m.id}
              className={`flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-md text-[11px] font-medium whitespace-nowrap transition-colors ${
                m.id === activeModelId
                  ? "bg-white text-foreground-900"
                  : "bg-foreground-950/60 text-white/70"
              }`}
            >
              <button
                type="button"
                onClick={() => onSelectModel?.(m.id)}
                className="cursor-pointer hover:opacity-80"
              >
                {m.label ?? m.id}
              </button>
              {onRemoveModel && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveModel(m.id);
                  }}
                  aria-label={`Remove ${m.label ?? m.id}`}
                  className={`w-4 h-4 flex items-center justify-center rounded-sm transition-colors cursor-pointer ${
                    m.id === activeModelId
                      ? "text-foreground-400 hover:text-red-500"
                      : "text-white/50 hover:text-white"
                  }`}
                >
                  <i className="ri-close-line text-[10px]"></i>
                </button>
              )}
            </div>
          ))}
          {onAddModel && (
            <button
              type="button"
              onClick={onAddModel}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium whitespace-nowrap bg-foreground-950/60 text-white/70 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-add-line text-[10px]"></i>
              Add
            </button>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={onToggleFullscreen}
        className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-lg bg-foreground-950/60 text-white hover:bg-foreground-950/80 transition-colors cursor-pointer z-10"
        aria-label={
          isViewerFullscreen ? "Exit fullscreen" : "Expand to fullscreen"
        }
      >
        <i
          className={`text-sm ${isViewerFullscreen ? "ri-fullscreen-exit-line" : "ri-fullscreen-line"}`}
        ></i>
      </button>
    </div>
  );
}

export default function IntakeMyfitScan() {
  const { phase, goTo, containerClasses } = usePageTransition();
  const [transitioning, setTransitioning] = useState(false);
  const totalSteps = getTotalIntakeSteps();

  const [inputMethod, setInputMethod] = useState<ScanInputMethod>("scan");

  // MyFit QR-scan path — completion is attested manually since it happens on the phone.
  const [scanConfirmed, setScanConfirmed] = useState(false);
  const handleConfirmScans = () => setScanConfirmed(true);

  // Upload-a-model path — completion follows automatically once there's at
  // least one model. Starts out with the built-in samples so the Upload tab
  // shows a working preview instead of an empty dropzone; the user can add
  // as many of their own models as they like and remove any of them
  // (including the samples) individually. Ported from
  // discovery/3d-model-viewer's FileDropZone: an .obj can arrive bundled
  // with a .mtl (matched by filename, or 1:1 if there's only one of each)
  // plus image files the .mtl references as textures.
  const [models, setModels] = useState<Model3DEntry[]>(PRESET_MODELS);
  const [activeModelId, setActiveModelId] = useState(PRESET_MODELS[0].id);
  const [uploadError, setUploadError] = useState("");
  const nextUploadIdRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const viewerContainerRef = useRef<HTMLDivElement>(null);
  // iOS Safari has no Fullscreen API for arbitrary elements (only <video> via
  // webkitEnterFullscreen) — isNativeFullscreen tracks the real API when
  // available, isFallbackFullscreen is a CSS-only stand-in for browsers that
  // lack it. Kept separate because the fallback is rendered via a portal
  // (see below), which the native path doesn't need.
  const [isNativeFullscreen, setIsNativeFullscreen] = useState(false);
  const [isFallbackFullscreen, setIsFallbackFullscreen] = useState(false);
  const isViewerFullscreen = isNativeFullscreen || isFallbackFullscreen;
  const [renderMode, setRenderMode] = useState<Model3DRenderMode>("solid");
  const [transformMode, setTransformMode] =
    useState<Model3DTransformMode>(null);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsNativeFullscreen(
        document.fullscreenElement === viewerContainerRef.current,
      );
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  const handleToggleFullscreen = () => {
    const el = viewerContainerRef.current;
    type LegacyFullscreenElement = HTMLElement & {
      webkitRequestFullscreen?: () => void;
    };
    const requestFs =
      el &&
      (el.requestFullscreen?.bind(el) ??
        (el as LegacyFullscreenElement).webkitRequestFullscreen?.bind(el));

    if (requestFs) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        requestFs();
      }
      return;
    }

    // No Fullscreen API at all (iOS Safari) — toggle the CSS-only fallback.
    setIsFallbackFullscreen((prev) => !prev);
  };

  const revokeModelUrls = (model: Model3DEntry) => {
    URL.revokeObjectURL(model.url);
    if (model.mtlUrl) URL.revokeObjectURL(model.mtlUrl);
    if (model.textureUrls) {
      for (const url of Object.values(model.textureUrls))
        URL.revokeObjectURL(url);
    }
  };

  // Revoking a static preset path is a harmless no-op (revokeObjectURL only
  // does anything for actual blob: URLs), so this can run unconditionally on
  // unmount for whatever models are still loaded at that point.
  const modelsRef = useRef(models);
  modelsRef.current = models;
  useEffect(() => {
    return () => {
      modelsRef.current.forEach(revokeModelUrls);
    };
  }, []);

  const handleFilesSelected = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    const stlFiles = files.filter((f) => f.name.toLowerCase().endsWith(".stl"));
    const objFiles = files.filter((f) => f.name.toLowerCase().endsWith(".obj"));
    const mtlFiles = files.filter((f) => f.name.toLowerCase().endsWith(".mtl"));
    const imageFiles = files.filter((f) => /\.(png|jpe?g|bmp)$/i.test(f.name));

    if (stlFiles.length === 0 && objFiles.length === 0) {
      setUploadError("Only STL and OBJ files are supported.");
      return;
    }
    setUploadError("");

    const newEntries: Model3DEntry[] = [];

    for (const obj of objFiles) {
      const base = obj.name.slice(0, -4);
      const mtl =
        mtlFiles.find((m) => m.name.slice(0, -4) === base) ??
        (objFiles.length === 1 && mtlFiles.length === 1
          ? mtlFiles[0]
          : undefined);
      const id = `uploaded-${nextUploadIdRef.current++}`;

      if (mtl) {
        const textureUrls: Record<string, string> = {};
        for (const tex of imageFiles) {
          textureUrls[tex.name.toLowerCase()] = URL.createObjectURL(tex);
        }
        newEntries.push({
          id,
          url: URL.createObjectURL(obj),
          type: "obj",
          mtlUrl: URL.createObjectURL(mtl),
          textureUrls:
            Object.keys(textureUrls).length > 0 ? textureUrls : undefined,
          label: [obj.name, mtl.name, ...imageFiles.map((f) => f.name)].join(
            ", ",
          ),
        });
      } else {
        newEntries.push({
          id,
          url: URL.createObjectURL(obj),
          type: "obj",
          label: obj.name,
        });
      }
    }

    for (const stl of stlFiles) {
      newEntries.push({
        id: `uploaded-${nextUploadIdRef.current++}`,
        url: URL.createObjectURL(stl),
        type: "stl",
        label: stl.name,
      });
    }

    if (newEntries.length === 0) return;
    setModels((prev) => [...prev, ...newEntries]);
    setActiveModelId(newEntries[newEntries.length - 1].id);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveModel = (id: string) => {
    const removed = models.find((m) => m.id === id);
    if (!removed) return;
    revokeModelUrls(removed);
    const next = models.filter((m) => m.id !== id);
    setModels(next);
    if (activeModelId === id) {
      setActiveModelId(next[0]?.id ?? "");
    }
    setUploadError("");
  };

  const scansCompleted =
    inputMethod === "scan" ? scanConfirmed : models.length > 0;
  const confirmed = scansCompleted;

  const handleContinue = () => {
    setTransitioning(true);
    setTimeout(() => {
      goTo("/intake/verification");
    }, 600);
  };

  return (
    <div className="h-screen flex flex-col bg-background-50">
      <IntakeTransitionOverlay />
      {/* Top Bar: Back + Progress */}
      <div className="flex-shrink-0 bg-white border-b border-background-100">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6">
          <div className="flex items-center gap-6 py-4">
            <Link
              to="/intake/account"
              className="inline-flex items-center gap-1.5 text-xs md:text-sm font-medium text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-sm"></i>
              </span>
              Back
            </Link>
          </div>

          <div className="pb-4">
            <IntakeProgressBar
              currentStep={totalSteps - 1}
              totalSteps={totalSteps}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`flex-1 flex items-start justify-center overflow-y-auto px-4 md:px-6 py-8 md:py-12 transition-all duration-500 ease-out relative ${containerClasses}`}
      >
        {/* Warm Background Blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-accent-100/20 blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full bg-primary-100/12 blur-3xl"></div>
        </div>

        <div className="w-full max-w-[1100px] mx-auto relative z-10">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-stretch">
            {/* LEFT PANEL — MyFit Scan Request */}
            <div className="lg:w-[55%] flex flex-col justify-center">
              <div className="bg-white rounded-2xl border border-background-100 p-5 md:p-10 animate-fadeSlideIn">
                {/* Header */}
                <div className="mb-7">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="w-9 h-9 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                      <i className="ri-camera-lens-line text-base"></i>
                    </span>
                    <h1 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
                      Capture Your Limb Scans with MyFit
                    </h1>
                  </div>
                  <p className="text-xs md:text-sm text-foreground-500 pl-12">
                    The physician needs precise 3D metrics and photos of your
                    residual limb(s) before your telehealth consult can be
                    scheduled.
                  </p>
                </div>

                {/* Why Scans Matter */}
                <div className="mb-6 px-5 py-4 rounded-xl bg-background-50 border border-background-100">
                  <div className="flex items-start gap-3 mb-3">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-primary-500 mt-0.5">
                      <i className="ri-information-line text-sm"></i>
                    </span>
                    <div>
                      <p className="text-xs md:text-sm text-foreground-700 leading-relaxed">
                        These scans let your physician assess socket fit, skin
                        condition, and surgical healing — all critical for your
                        prosthetic prescription. Without them, the physician
                        cannot complete your evaluation.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg bg-green-50 border border-green-100">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-green-600">
                        <i className="ri-checkbox-circle-line text-sm"></i>
                      </span>
                      <span className="text-[11px] text-green-700 font-medium">
                        No special equipment
                      </span>
                    </div>
                    <div className="flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg bg-green-50 border border-green-100">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-green-600">
                        <i className="ri-checkbox-circle-line text-sm"></i>
                      </span>
                      <span className="text-[11px] text-green-700 font-medium">
                        Less than 5 minutes
                      </span>
                    </div>
                    <div className="flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg bg-green-50 border border-green-100">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-green-600">
                        <i className="ri-checkbox-circle-line text-sm"></i>
                      </span>
                      <span className="text-[11px] text-green-700 font-medium">
                        Done from your couch
                      </span>
                    </div>
                  </div>
                </div>

                {/* Input Method Toggle */}
                <div className="flex gap-2 mb-4 p-1 rounded-xl bg-background-100">
                  <button
                    type="button"
                    onClick={() => setInputMethod("scan")}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                      inputMethod === "scan"
                        ? "bg-white text-foreground-900 shadow-sm"
                        : "text-foreground-500 hover:text-foreground-700"
                    }`}
                  >
                    <i className="ri-qr-scan-2-line text-sm"></i>
                    Scan with MyFit
                  </button>
                  <button
                    type="button"
                    onClick={() => setInputMethod("upload")}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                      inputMethod === "upload"
                        ? "bg-white text-foreground-900 shadow-sm"
                        : "text-foreground-500 hover:text-foreground-700"
                    }`}
                  >
                    <i className="ri-upload-cloud-2-line text-sm"></i>
                    Upload a 3D model
                  </button>
                </div>

                {/* MyFit Scan Card */}
                {inputMethod === "scan" ? (
                  <div className="rounded-2xl border-2 border-accent-300 bg-accent-50/40 p-4 md:p-7 mb-6">
                    <h3 className="font-heading text-sm md:text-base font-bold text-foreground-950 mb-2">
                      Scan the QR code to get started
                    </h3>
                    <p className="text-[11px] md:text-xs text-foreground-500 mb-5 leading-relaxed">
                      Open your phone&apos;s camera and point it at this QR
                      code. The MyFit app will guide you through capturing all
                      the photos and 3D metrics the physician needs — it walks
                      you through every angle step by step so you can&apos;t
                      mess it up.
                    </p>

                    <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-6 mb-5">
                      {/* QR + Phone — paired side by side on all screens */}
                      <div className="flex items-center gap-4 sm:gap-6 self-center sm:self-auto flex-shrink-0">
                        {/* QR Code */}
                        <div className="w-24 h-24 sm:w-28 sm:h-28 flex-shrink-0 bg-white rounded-xl border-2 border-background-200 p-2 sm:p-2.5 flex items-center justify-center">
                          <div className="grid grid-cols-7 gap-[2px] w-full h-full">
                            {Array.from({ length: 49 }).map((_, i) => {
                              const row = Math.floor(i / 7);
                              const col = i % 7;
                              const isFinder =
                                (row < 2 && col < 2) ||
                                (row < 2 && col > 4) ||
                                (row > 4 && col < 2);
                              const isInner =
                                row >= 2 && row <= 4 && col >= 2 && col <= 4;
                              const isActive =
                                isFinder || (!isInner && Math.random() > 0.5);
                              return (
                                <div
                                  key={i}
                                  className={`rounded-[1px] ${isActive ? "bg-foreground-900" : "bg-transparent"}`}
                                />
                              );
                            })}
                          </div>
                        </div>

                        {/* Phone Mockup */}
                        <div className="flex-shrink-0 relative">
                          <div className="w-[64px] sm:w-[72px] h-[114px] sm:h-[128px] bg-foreground-900 rounded-[18px] sm:rounded-[20px] p-1">
                            <div className="w-full h-full bg-background-50 rounded-[14px] sm:rounded-[16px] flex flex-col items-center justify-center gap-0.5">
                              <span className="w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center text-primary-500">
                                <i className="ri-scan-2-line text-[10px] sm:text-xs"></i>
                              </span>
                              <span className="text-[6px] sm:text-[7px] font-semibold text-foreground-700 text-center leading-tight">
                                MyFit 3D
                              </span>
                              <span className="text-[5px] sm:text-[6px] text-foreground-400 text-center leading-tight">
                                Scan App
                              </span>
                              <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-accent-500 flex items-center justify-center mt-0.5 sm:mt-1">
                                <span className="w-3 sm:w-3.5 h-3 sm:h-3.5 flex items-center justify-center text-foreground-950">
                                  <i className="ri-arrow-right-line text-[6px] sm:text-[7px]"></i>
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Steps — stack below QR/Phone on mobile, beside on desktop */}
                      <div className="space-y-2.5 sm:space-y-3">
                        <div className="flex items-center gap-2.5">
                          <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-200 text-accent-700 flex-shrink-0 text-[10px] font-bold">
                            1
                          </span>
                          <span className="text-[11px] text-foreground-600 leading-tight whitespace-nowrap">
                            Scan code with phone
                          </span>
                        </div>
                        <div className="flex items-center gap-2.5">
                          <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-200 text-accent-700 flex-shrink-0 text-[10px] font-bold">
                            2
                          </span>
                          <span className="text-[11px] text-foreground-600 leading-tight whitespace-nowrap">
                            MyFit guides photo capture
                          </span>
                        </div>
                        <div className="flex items-center gap-2.5">
                          <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-200 text-accent-700 flex-shrink-0 text-[10px] font-bold">
                            3
                          </span>
                          <span className="text-[11px] text-foreground-600 leading-tight whitespace-nowrap">
                            Results sent to physician
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Privacy Note */}
                    <div className="flex items-start gap-2 px-3 sm:px-4 py-3 rounded-xl bg-background-50 border border-background-100">
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-foreground-400 mt-0.5">
                        <i className="ri-shield-check-line text-[10px]"></i>
                      </span>
                      <p className="text-[10px] text-foreground-500 leading-relaxed">
                        All images and 3D data are encrypted end-to-end and
                        shared only with your assigned physician. HIPAA
                        compliant. You can delete your data at any time.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-2xl border-2 border-accent-300 bg-accent-50/40 p-4 md:p-7 mb-6">
                    <h3 className="font-heading text-sm md:text-base font-bold text-foreground-950 mb-2">
                      Upload your 3D model file
                    </h3>
                    <p className="text-[11px] md:text-xs text-foreground-500 mb-5 leading-relaxed">
                      Already have a 3D scan of your residual limb? Upload the
                      STL or OBJ file directly instead of scanning with the
                      MyFit app — you can preview it below before submitting.
                    </p>

                    {models.length === 0 ? (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full flex flex-col items-center justify-center gap-2 px-5 py-10 rounded-xl border-2 border-dashed border-accent-300 bg-white hover:border-accent-400 hover:bg-accent-50/60 transition-all cursor-pointer mb-3"
                      >
                        <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                          <i className="ri-upload-cloud-2-line text-lg"></i>
                        </span>
                        <span className="text-xs font-semibold text-foreground-700">
                          Click to upload a model file
                        </span>
                        <span className="text-[10px] text-foreground-400">
                          STL, or OBJ — optionally select its .mtl and texture
                          files together for full materials
                        </span>
                      </button>
                    ) : (
                      <div className="mb-3">
                        {isFallbackFullscreen ? (
                          createPortal(
                            <div className="fixed inset-0 z-[999] bg-background-50">
                              <ModelViewerPanel
                                models={models}
                                activeModelId={activeModelId}
                                onSelectModel={setActiveModelId}
                                onRemoveModel={handleRemoveModel}
                                onAddModel={() => fileInputRef.current?.click()}
                                renderMode={renderMode}
                                setRenderMode={setRenderMode}
                                transformMode={transformMode}
                                setTransformMode={setTransformMode}
                                isViewerFullscreen={isViewerFullscreen}
                                onToggleFullscreen={handleToggleFullscreen}
                              />
                            </div>,
                            document.body,
                          )
                        ) : (
                          <div
                            ref={viewerContainerRef}
                            className={`relative rounded-xl overflow-hidden border border-background-200 bg-background-50 ${
                              isNativeFullscreen
                                ? "h-screen w-screen rounded-none"
                                : "h-64"
                            }`}
                          >
                            <ModelViewerPanel
                              models={models}
                              activeModelId={activeModelId}
                              onSelectModel={setActiveModelId}
                              onRemoveModel={handleRemoveModel}
                              onAddModel={() => fileInputRef.current?.click()}
                              renderMode={renderMode}
                              setRenderMode={setRenderMode}
                              transformMode={transformMode}
                              setTransformMode={setTransformMode}
                              isViewerFullscreen={isViewerFullscreen}
                              onToggleFullscreen={handleToggleFullscreen}
                            />
                          </div>
                        )}
                        <div className="mt-2 px-1">
                          <span className="text-[11px] text-foreground-500 truncate flex items-center gap-1.5">
                            <i className="ri-file-3-line"></i>
                            {models.length} model
                            {models.length === 1 ? "" : "s"} loaded
                          </span>
                        </div>
                      </div>
                    )}

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".stl,.obj,.mtl,.png,.jpg,.jpeg,.bmp"
                      multiple
                      className="hidden"
                      onChange={(e) => handleFilesSelected(e.target.files)}
                    />

                    {uploadError && (
                      <p className="text-[11px] text-red-500 mb-3">
                        {uploadError}
                      </p>
                    )}

                    {/* Privacy Note */}
                    <div className="flex items-start gap-2 px-3 sm:px-4 py-3 rounded-xl bg-background-50 border border-background-100">
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-foreground-400 mt-0.5">
                        <i className="ri-shield-check-line text-[10px]"></i>
                      </span>
                      <p className="text-[10px] text-foreground-500 leading-relaxed">
                        Uploaded files are shared only with your assigned
                        physician. HIPAA compliant. You can delete your data at
                        any time.
                      </p>
                    </div>
                  </div>
                )}

                {/* What Happens After */}
                <div className="mb-6 px-5 py-4 rounded-xl bg-primary-50 border border-primary-100">
                  <div className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-primary-500 mt-0.5">
                      <i className="ri-arrow-right-circle-line text-sm"></i>
                    </span>
                    <div>
                      <p className="text-[11px] md:text-xs font-semibold text-primary-800 mb-1">
                        Once your scans are submitted
                      </p>
                      <p className="text-[11px] text-primary-700 leading-relaxed">
                        Your physician will review the 3D metrics alongside the
                        rest of your intake profile. You&apos;ll then be able to
                        schedule your telehealth consultation and get your
                        prescription signed off — no clinic visit required.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Scans Completion Confirmation */}
                {!scansCompleted ? (
                  inputMethod === "scan" ? (
                    <div className="mb-2">
                      <button
                        type="button"
                        onClick={handleConfirmScans}
                        className="w-full flex items-center gap-3 px-5 py-4 rounded-xl border-2 border-dashed border-background-200 bg-background-50 hover:border-green-300 hover:bg-green-50/50 transition-all cursor-pointer group"
                      >
                        <span className="w-6 h-6 flex items-center justify-center rounded-md border-2 border-background-300 text-transparent group-hover:border-green-400 group-hover:text-green-500 transition-all flex-shrink-0">
                          <i className="ri-check-line text-sm"></i>
                        </span>
                        <div className="text-left">
                          <p className="text-xs font-semibold text-foreground-700 group-hover:text-green-700 transition-colors">
                            I&apos;ve completed my scans in the MyFit app
                          </p>
                          <p className="text-[10px] text-foreground-400 group-hover:text-green-500 transition-colors">
                            Confirm you&apos;ve followed the guided capture in
                            the MyFit app before continuing
                          </p>
                        </div>
                      </button>
                    </div>
                  ) : (
                    <div className="mb-2 px-5 py-4 rounded-xl border-2 border-dashed border-background-200 bg-background-50">
                      <p className="text-xs font-semibold text-foreground-500">
                        Waiting for a model file
                      </p>
                      <p className="text-[10px] text-foreground-400">
                        Upload an STL or OBJ file above to continue
                      </p>
                    </div>
                  )
                ) : (
                  <div className="mb-2 px-5 py-4 rounded-xl bg-green-50 border border-green-200 animate-fadeSlideIn">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 flex items-center justify-center rounded-full bg-green-500 text-white flex-shrink-0">
                        <i className="ri-check-line text-sm"></i>
                      </span>
                      <div>
                        <p className="text-xs md:text-sm font-bold text-green-800">
                          {inputMethod === "scan"
                            ? "Scans marked as completed"
                            : "Model uploaded"}
                        </p>
                        <p className="text-[10px] text-green-600">
                          {inputMethod === "scan"
                            ? "Your scan data has been confirmed — you're ready to move on"
                            : "Your 3D model has been received — you're ready to move on"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* CTA */}
                {!confirmed ? (
                  <div>
                    <button
                      type="button"
                      disabled
                      className="w-full py-4 rounded-xl bg-background-200 text-foreground-400 text-sm md:text-base font-bold cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-lock-line text-sm"></i>
                      </span>
                      Confirm Scans First to Continue
                    </button>
                    {!transitioning && (
                      <button
                        type="button"
                        onClick={handleContinue}
                        className="w-full mt-3 py-3 rounded-xl border border-background-200 bg-transparent text-foreground-500 text-xs md:text-sm font-medium hover:border-foreground-300 hover:text-foreground-700 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
                      >
                        <span className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-skip-forward-line text-sm"></i>
                        </span>
                        Skip this step — I&apos;ll do it later
                      </button>
                    )}
                  </div>
                ) : !transitioning ? (
                  <button
                    type="button"
                    onClick={handleContinue}
                    className="w-full py-4 rounded-xl bg-primary-500 text-background-50 text-sm md:text-base font-bold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 animate-fadeSlideIn"
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-sm"></i>
                    </span>
                    Continue to Verification
                  </button>
                ) : (
                  <div className="w-full py-4 rounded-xl bg-green-50 border border-green-200 flex items-center justify-center gap-3 animate-fadeSlideIn">
                    <span className="w-5 h-5 flex items-center justify-center text-green-600">
                      <i className="ri-loader-4-line text-sm animate-spin"></i>
                    </span>
                    <span className="text-sm font-bold text-green-700">
                      Taking you to the next step...
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT PANEL */}
            <div className="lg:w-[45%] flex">
              <div className="bg-white rounded-2xl border border-background-100 p-6 md:p-8 flex flex-col w-full animate-slideUpIn">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-100 text-[10px] font-semibold text-accent-800 mb-4 self-start">
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-play-circle-line text-[9px]"></i>
                  </span>
                  About MyFit 3D
                </span>

                <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-3">
                  {adPanelContent.myfitScan.title}
                </h3>

                <div className="rounded-xl overflow-hidden mb-4 flex-shrink-0 aspect-video">
                  <iframe
                    src="https://www.youtube.com/embed/EpFrKWt_9HQ?autoplay=1&mute=1&loop=1&playlist=EpFrKWt_9HQ&modestbranding=1&rel=0&enablejsapi=1"
                    title="MyFit 3D Scanning Overview"
                    allow="autoplay; mute; accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                    frameBorder="0"
                  />
                </div>

                <p className="text-sm text-foreground-500 leading-relaxed flex-1">
                  {adPanelContent.myfitScan.subtitle}
                </p>

                <div className="mt-5 flex items-center gap-3 px-4 py-3 rounded-xl bg-accent-50 border border-accent-100">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-accent-600">
                    <i className="ri-smartphone-line text-sm"></i>
                  </span>
                  <p className="text-[11px] text-accent-800 leading-relaxed font-medium">
                    Works on iPhone and Android — no downloads required, opens
                    right in your browser.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Compliance Footer */}
      <div className="w-full border-t border-background-100 bg-background-50">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 py-4">
          <div className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-foreground-400 mt-0.5">
              <i className="ri-information-line text-[10px]"></i>
            </span>
            <p className="text-[10px] text-foreground-400 leading-relaxed max-w-3xl">
              Notice: This platform is an administrative optimization tool. All
              AI-generated functional assessments, financial forecasts,
              wholesale cost aggregations, advertising recommendations, 3D
              metric visualizations, and documentation templates are strictly
              informational. Final clinical specifications, billing submittals,
              prescriptions, and custom fabrication workflows require formal,
              independent review and explicit sign-off by a licensed physician
              and a Certified Prosthetist prior to patient care.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
